import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import {
  PositionStatus,
  UIFilter,
  UIFilterGroup,
  UniqueIdGenerator,
} from 'src/app/people/quick-build.service';
import { ColumnType } from 'src/app/shared/dw-column';
import { getPeopleColumnByName } from '../../people-columns';
import { Log } from 'src/app/logger.service';
interface RemoveGroupEvent {
  group: UIFilterGroup;
  parentGroup?: UIFilterGroup;
}

@Component({
    selector: 'app-filter-group',
    templateUrl: './filter-group.component.html',
    styleUrls: ['./filter-group.component.scss'],
    standalone: false
})
export class FilterGroupComponent {
  @Input() group: UIFilterGroup = {
    id: UniqueIdGenerator.generate(),
    operator: 'and',
    filters: [],
  };
  @Input() isRoot: boolean = false;
  @Input() parentGroup?: UIFilterGroup;
  @Input() depth: number = 0;
  @Input() parentOperator: string = '';

  @Output() filterAdded = new EventEmitter<{
    component: FilterGroupComponent;
    isJobsGroup: boolean;
  }>();
  @Output() chartAdded = new EventEmitter<{
    component: FilterGroupComponent;
    isJobsGroup: boolean;
  }>();
  @Output() removeGroup = new EventEmitter<RemoveGroupEvent>();
  @Output() groupAdded = new EventEmitter<UIFilterGroup>();

  editingGroupName = false;
  @ViewChild('groupNameInput') groupNameInput!: ElementRef;

  ngOnInit(): void {
    if (!this.group) {
      this.group = { id: UniqueIdGenerator.generate(), operator: 'and', filters: [] };
    }

    if (this.group.isJobsGroup) {
      if (!this.group.jobsGroupType) {
        this.group.jobsGroupType = 'any';
      }

      if (!this.group.positionStatus) {
        this.group.positionStatus = PositionStatus.All;
      }
    }

    if (!this.group.name) {
      this.group.name = 'Grouped Job Filters';
    }
  }

  matchJobsOptions = [
    {
      value: 'any',
      displayValue: 'Any Jobs',
    },
    {
      value: 'active',
      displayValue: 'Only Active',
    },
    {
      value: 'ended',
      displayValue: 'Only Ended',
    },
  ];
  matchJobsSelected = this.matchJobsOptions[0];
  tenureOptions = [
    {
      value: PositionStatus.All,
      displayValue: 'All',
    },
    {
      value: PositionStatus.First,
      displayValue: 'First Job at Company',
    },
    {
      value: PositionStatus.Promotion,
      displayValue: 'Promotions',
    },
    {
      value: PositionStatus.Last,
      displayValue: 'Last Job at Company',
    },
  ];

  // add a new simple filter, it opens filter dialog selector
  addFilter(): void {
    this.filterAdded.emit({ component: this, isJobsGroup: this.group.isJobsGroup || false });
  }

  isSimpleFilter(item: UIFilter | UIFilterGroup): boolean {
    const filter = (item as UIFilter).filter;
    if (filter === undefined) {
      return false;
    }

    const col = getPeopleColumnByName(filter.field!);
    return col.type !== ColumnType.companySearch && col.type !== ColumnType.subFilters;
  }

  isCompanySearchFilter(item: UIFilter | UIFilterGroup): boolean {
    const filter = (item as UIFilter).filter;
    if (filter === undefined) {
      return false;
    }

    const col = getPeopleColumnByName(filter.field!);
    return col.type === ColumnType.companySearch;
  }

  isSubfiltersFilter(item: UIFilter | UIFilterGroup): boolean {
    const filter = (item as UIFilter).filter;
    if (filter === undefined) {
      return false;
    }

    const col = getPeopleColumnByName(filter.field!);
    return col.type === ColumnType.subFilters;
  }

  isFilterGroup(item: UIFilter | UIFilterGroup): item is UIFilterGroup {
    return (item as UIFilterGroup).operator !== undefined;
  }

  onRemoveItem(index: number) {
    this.group.filters.splice(index, 1);
  }

  addGroup() {
    if (this.group && this.group.filters) {
      const newGroup: UIFilterGroup = {
        id: UniqueIdGenerator.generate(),
        operator: 'and',
        filters: [],
        isJobsGroup: false, // default behavior for new groups
      };
      this.group.filters.push(newGroup);
      this.groupAdded.emit();
    } else {
      Log.e('Attempted to add a group, but the group or filters array is undefined.');
    }
  }

  addChart() {
    this.chartAdded.emit({ component: this, isJobsGroup: this.group.isJobsGroup || false });
  }

  removeChart(columnName: string) {
    if (this.group.fieldsToChart) {
      this.group.fieldsToChart = this.group.fieldsToChart.filter(
        (field) => field.columnName !== columnName
      );
    }
  }

  onDeleteGroup() {
    this.removeGroup.emit({ group: this.group, parentGroup: this.parentGroup });
  }

  isEmptyGroup(): boolean {
    if (!this.group.filters) {
      return true;
    }
    return this.group.filters.length === 0;
  }

  isEvenDepth(): boolean {
    return this.depth % 2 === 0;
  }

  toggleEdit() {
    this.editingGroupName = true;
    setTimeout(() => {
      this.groupNameInput.nativeElement.focus();
      this.groupNameInput.nativeElement.select(); // select entire text
    }, 0);
  }

  saveGroupName() {
    this.editingGroupName = false;
  }
}
